import {
  ApexService,
  ApexServiceOptions,
  GetApexAccountRequestsCountOptions,
  GetApexAccountRequestOptions,
  GetApexAccountRequestsOptions,
  // responses
  ApexAccountResponse,
  ApexAccountRequestHeaderResponse,
  ApexInvestigationHistoryRequestOption,
  ApexInvestigationHistoryResponse,
  KycDocumentStatusOptions,
  ApexLabelResponse,
  AccountRequestFilterResponse,
  LinkKycDocumentOptions,
  ApexAccountInvestigationOptions,
  ApexAccountInvestigationCommentOptions,
  ApexAccountInvestigationPostOptions,
  ApexInvestigationResponse,
  ImageUploadForKYCProcessOptions,
  AccountInvestigationResponse,
} from '@digital-advice/ms-apex-sdk';
import {apiKey, getLogger, resolveEndpointForServiceToken, interceptors, sourceInfo} from '@advisor/services/sdk';
import {DefaultRequestOptions, MicroserviceResponse} from '@digital-advice/ms-core-sdk';

const svcOptions = {hasServiceTokenEndpoint: false};

export const defaultOptions: ApexServiceOptions = {
  logger: getLogger(),
  apiKey,
  endpoint: resolveEndpointForServiceToken(svcOptions.hasServiceTokenEndpoint),
  interceptors,
  sourceInfo,
};

export default class ApexServices {
  static async sdkService(options: ApexServiceOptions = defaultOptions): Promise<ApexService> {
    const sdkService = await new ApexService(options);

    return sdkService;
  }

  /**
   * Get status wise count of KYC accounts
   *
   * @async
   * @param options - A {@link GetApexAccountRequestsCountOptions} object.
   * @returns A promise of a Map<string, number>.
   */
  static async getApexAccountRequestsCount(options: GetApexAccountRequestsCountOptions): Promise<Map<string, number>> {
    const service = await this.sdkService(defaultOptions);

    return service.getApexAccountRequestsCount(options);
  }

  /**
   * Gateway endpoint: getAccountRequestForAdvisor
   * @param {Object} {riaId,status}
   */

  static async getAccountRequestForAdvisor(
    payload: GetApexAccountRequestOptions
  ): Promise<MicroserviceResponse<ApexAccountResponse>> {
    const service = await this.sdkService(defaultOptions);

    return service.getAccountRequestForAdvisor(payload);
  }

  /**

   * Get Apex account request info, info from account_request_header table join with apex_reject_reason_mapping
   *
   * @async
   * @param options - A {@link GetApexAccountRequestsOptions} object.
   * @returns A promise of a {@link ApexAccountRequestHeaderResponse} object.
   */
  static async getApexAccountRequest(options: GetApexAccountRequestsOptions): Promise<ApexAccountRequestHeaderResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.getApexAccountRequest(options);
  }
  /**
   * Add investigation history event in apex_account_investigation_history
   *
   * @async
   * @param options - A {@link ApexInvestigationHistoryRequestOption} object.
   * @returns A promise of a {@link ApexInvestigationHistoryResponse} object.
   */

  static async addApexAccountInvestigationHistory(
    payload: ApexInvestigationHistoryRequestOption
  ): Promise<ApexInvestigationHistoryResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.addApexAccountInvestigationHistory(payload);
  }

  /**
   * Update KYC document Status with AccountNumber.
   *
   * @async
   * @param options - A {@link KycDocumentStatusOptions} object.
   * @returns A promise of a Apex response.
   */
  static async updateKycDocumentStatus(options: KycDocumentStatusOptions): Promise<void> {
    const service = await this.sdkService(defaultOptions);

    return service.updateKycDocumentStatus(options);
  }

  /**
   * Add investigation history event in apex_account_investigation_history
   *
   * @async
   * @param options - A {@link DefaultRequestOptions} object.
   * @returns A promise of a {@link ApexLabelResponse} object.
   */

  static async getApexLabels(payload: DefaultRequestOptions): Promise<ApexLabelResponse[]> {
    const service = await this.sdkService(defaultOptions);

    return service.getApexLabels(payload);
  }

  /**
   * Get accountNumbers for accountrequests.
   *
   * @async
   * @param options - A {@link GetApexAccountRequestOptions} object.
   * @returns A promise.
   */
  static async getAccountRequestAccountNumbersForAdvisor(
    options: GetApexAccountRequestOptions
  ): Promise<AccountRequestFilterResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.getAccountRequestAccountNumbersForAdvisor(options);
  }

  static async getAccountRequestClientList(options: GetApexAccountRequestOptions): Promise<AccountRequestFilterResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.getAccountRequestClientList(options);
  }

  /**
   * Get ApexRejectReasons for accountrequests.
   *
   * @async
   * @param options - A {@link GetApexAccountRequestOptions} object.
   * @returns A promise.
   */
  static async getAccountRequestRejectReasons(
    options: GetApexAccountRequestOptions
  ): Promise<AccountRequestFilterResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.getAccountRequestRejectReasons(options);
  }

  /* Get apex account investigation with AccountId.
   * Links a KYC document to the request ID.  If matching document exists, it will be de-activated..
   *
   * @async
   * @param options - A {@link LinkKycDocumentOptions} object.
   * @returns A promise.
   */
  static async linkKycDocument(options: LinkKycDocumentOptions): Promise<void> {
    const service = await this.sdkService(defaultOptions);

    return service.linkKycDocument(options);
  }

  /**
   * Get apex account investigation with AccountId.
   *
   * @async
   * @param options - A {@link ApexAccountInvestigationOptions} object.
   * @returns A promise.
   */
  static async getAccountInvestigationByAccountId(
    options: ApexAccountInvestigationOptions
  ): Promise<AccountInvestigationResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.getAccountInvestigationByAccountId(options);
  }

  /**
   * Update apex account investigation comments by AccountId.
   *
   * @async
   * @param options - A {@link ApexAccountInvestigationCommentOptions} object.
   * @returns A {@link AccountInvestigationResponse} promise.
   */
  static async updateAccountInvestigationCommentByAccountId(
    options: ApexAccountInvestigationCommentOptions
  ): Promise<AccountInvestigationResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.updateAccountInvestigationCommentByAccountId(options);
  }

  /**
   * Update apex account investigation.
   *
   * @async
   * @param options - A {@link ApexAccountInvestigationPostOptions} object.
   * @returns A {@link AccountInvestigationResponse} promise.
   */
  static async updateAccountInvestigationOnApex(
    options: ApexAccountInvestigationPostOptions
  ): Promise<ApexInvestigationResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.updateAccountInvestigationOnApex(options);
  }

  /**
   * Update apex account investigation status as reject.
   *
   * @async
   * @param options - A {@link ApexAccountInvestigationPostOptions} object.
   * @returns A {@link AccountInvestigationResponse} promise.
   */
  static async updateRejectAccountInvestigationStatusOnApex(
    options: ApexAccountInvestigationPostOptions
  ): Promise<ApexInvestigationResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.updateRejectAccountInvestigationStatusOnApex(options);
  }

  /**
   * ImageUpload for Sketch API calls sequence KYC process.
   * Moved image param from request-param to request-body
   *
   * @async
   * @param options - A {@link ImageUploadForKYCProcessOptions} object.
   * @returns A promise of a Apex response.
   */
  static async imageUploadForKYCprocess(options: ImageUploadForKYCProcessOptions): Promise<MicroserviceResponse<string>> {
    const service = await this.sdkService(defaultOptions);

    return service.imageUploadForKYCprocess(options);
  }
}
